/**
 * SchoolContext
 *
 * Provides school information throughout the application.
 * Fetches school data from API using TanStack Query with caching.
 * Only fetches when user is authenticated.
 *
 * @module context/SchoolContext
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, setSchoolName, type ApiResponse } from '../services/api';
import { useAuth } from './AuthContext';

/**
 * Days of the week for working days configuration.
 */
export type WeekDay =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/**
 * Attendance marking mode.
 *
 * ⚠️ [PERIOD-WISE P0] `'BOTH'` REMOVED — the server enum has never had it.
 * `AttendanceMode` in `schema.prisma:32` is `DAILY | PERIOD_WISE`, `school.schemas.ts:38` validates
 * exactly those two, and `markStudentAttendance` branches on them alone. A third member here meant the
 * client's type admitted a value the server would reject, so any code narrowing on it would have been
 * unreachable-but-compiling — the shape that lets a dead branch look finished.
 *
 * ⚠️ **NOTHING WAS NARROWING ON IT**, which is why this is a type fix and not a behaviour change:
 * `AdminSettings.tsx:557` already types its save as `'DAILY' | 'PERIOD_WISE'`, and the only other
 * `'BOTH'` strings in the client belong to `resultPassRule`, an unrelated field.
 *
 * ⚠️ Whether a school should be able to run a daily register AND period marks together is a real
 * product question — it is **deferred with P4**, and if it is ever taken it needs a rule for which
 * wins when they disagree. Reinstating this member without that rule would be inventing the feature's
 * type before its behaviour.
 */
export type AttendanceMode = 'DAILY' | 'PERIOD_WISE';

/**
 * Feature flags for school-specific features.
 */
/**
 * The school's feature flags — MIRRORS `server/src/config/feature-flags.ts` and nothing else.
 *
 * [TEACHER-WARD-0] Ten phantom keys were deleted here: `transport`, `hostel`, `aiInsights`,
 * `achievements`, `connections`, `onlinePayment`, `smsNotifications`, `whatsappNotifications`,
 * `parentApp`, `biometricAttendance`. NONE existed in the server registry, so the server never sent
 * them, `hasFeature()` on them always returned false, and every one had ZERO `hasFeature` calls and
 * zero direct reads (verified before deleting). They were not switches — they were a list of things
 * someone once intended to build, sitting in a type that reads like configuration. Keeping them
 * costs nothing until the day somebody writes `hasFeature('transport')`, gets `false` forever, and
 * goes looking for the toggle that turns it on.
 *
 * KEEP THIS LIST EQUAL TO THE SERVER REGISTRY. It is hand-maintained — the server generates its
 * owner matrix, tenant zod schema and enforcement gate from one entry, but this file does not.
 */
export interface FeatureFlags {
  /** Enable library management */
  library: boolean;
  /** Enable olympiad/competition features */
  olympiad: boolean;
  /** Enable teacher ratings by students */
  teacherRating: boolean;
  /**
   * ELECTIVES-1: opt-in elective-subjects console. The server gates it with requireFeatureOptIn, so it
   * is OFF for every school until the owner turns it on — NOT grandfathered (an absent key means off).
   */
  electiveSubjects: boolean;
  /**
   * CONNECTIONS-1: the server's canonical key. `requireFeature('studentConnections')` gates the
   * whole connections router, `createSchool` defaults it to TRUE, and the server GRANDFATHERS a
   * missing key to allowed. See `hasFeature` for why the absent-key default matters.
   */
  studentConnections: boolean;
  /** DIARY-1: enable the digital school diary (opt-in — default OFF, unlike grandfathered flags) */
  diary: boolean;
  /** STAFF-ATT: enable staff self-attendance (opt-in — default OFF, like diary) */
  staffAttendance: boolean;
  /**
   * TT-CONFIG-2: per-SECTION periods-per-week (5A can need 5 maths a week while 5B needs 7).
   * Opt-in — the server uses requireFeatureOptIn, so an absent key means OFF and a school that
   * never turns it on keeps today's class-level fan-out byte-for-byte.
   */
  sectionPeriods: boolean;
  /**
   * TT-CONFIG-2: named teacher tiers with an eligible class set (a school's own "junior"/"senior").
   * Opt-in, and separate from `sectionPeriods` on purpose — the two have different blast radii, so
   * a school can roll one back without losing the other.
   */
  teacherTiers: boolean;

  /**
   * ONBOARD-FEE-1 — pre-Aisistoo opening balances, for a school joining MID-session.
   * Opt-in (absent key = OFF server-side via requireFeatureOptIn).
   */
  feeOpeningBalance: boolean;

  /**
   * TEACHER-WARD-0 — show a staff member that their mobile also carries a parent login.
   * Tier 0: discoverability only, no new read surface. Opt-in (absent key = OFF server-side).
   */
  teacherWardHint: boolean;

  /**
   * OCCASION-CARDS-1 — birthday / festival / annual-day cards. Three flags rather than one because the
   * occasions differ in who authors them and how far they reach; see the server registry for the full
   * reasoning. All three opt-in (absent key = OFF server-side via requireFeatureOptIn).
   */
  occasionCards: boolean;
  occasionFestivalCards: boolean;
  occasionAnnualDay: boolean;

  /*
   * 🔴 [TT-WIZARD-1 S7] THE FOUR TIMETABLE FLAGS, TOGETHER — THREE OF THEM NEVER TYPED HERE AT ALL.
   * `subjectHierarchy` was added at TT-HIERARCHY-3 Slice 8 and its three neighbours from the SAME
   * server registry never were, so `hasFeature('timetableSoftPack')` was a COMPILE ERROR: a screen
   * could not even ask whether the feature behind its own control was running. Same shape as
   * SETTINGS-GAPS-1 A, where `electiveSubjects` was enforced in five server sites and absent from
   * both write paths that could switch it on.
   *
   * ⚠️ They are grouped because they are one subsystem's switches, and because `subjectHierarchy`
   * had drifted into the middle of the OCCASION-CARDS-1 block — where its docblock reads as if it
   * belonged to the three occasion keys below it.
   *
   * All four are opt-in server-side EXCEPT `ttDiagnostics`, which carries `newSchoolDefault` — the
   * one accepted deviation, declared in the registry rather than buried in `createSchool`.
   */
  /** TT-HIERARCHY-3 Slice 8 — absent ⇒ OFF, no new-school default. */
  subjectHierarchy: boolean;
  /** TT-HIERARCHY-3 Slice 1 — per-slot blocked-reason attribution. ⚠️ ON for NEW schools, off for existing. */
  ttDiagnostics: boolean;
  /** TT-HIERARCHY-3 Slice 10b — the soft-constraint post-pass. Absent ⇒ OFF. */
  timetableSoftPack: boolean;
  /** TT-HIERARCHY-3 Slice 9 — derived branch-point readiness warnings. Absent ⇒ OFF. */
  timetableBranching: boolean;

  /**
   * PHOTO-1 — profile photographs, split by population because who approves them differs and so does
   * what is at stake: a staff headshot is an adult's own picture approved by the office, a child's is
   * approved by their class teacher. A school may reasonably want faces in the staff directory and no
   * photographs of children at all. Both opt-in (absent key = OFF server-side).
   */
  staffPhotos: boolean;
  studentPhotos: boolean;

  /**
   * REQ-AUG-26 Band 3 — opt-in services, one flag per service (ruling Q7).
   *
   * ⚠️ `transport` AND `hostel` ARE THE SAME TWO KEY NAMES TEACHER-WARD-0 DELETED FROM THIS FILE AS
   * PHANTOMS, AND THE DIFFERENCE IS THE WHOLE POINT OF THAT DELETION. Then they were client-only,
   * with no server registry entry, so `hasFeature('transport')` returned false forever and there was
   * no toggle anywhere that could change it. They are re-added here ONLY because Band 3 added the
   * matching entries to `server/src/config/feature-flags.ts` — the owner console now renders them and
   * `requireFeatureOptIn` now enforces them. A key here without one there is a lie; the header's
   * "KEEP THIS LIST EQUAL TO THE SERVER REGISTRY" is what makes that checkable.
   */
  transport: boolean;
  hostel: boolean;
  coaching: boolean;

  /**
   * BOARD-RESULT-1 — external board results (CBSE / BSEB / … gazette).
   *
   * Added here because the server registry has the matching entry and `requireFeatureOptIn` enforces
   * it — the rule this file's header states: "a key here without one there is a lie".
   *
   * ⚠️ NOTED, NOT FIXED, because it is not this epic's to change: `houses` (REQ-AUG-26 Band 5) IS in the
   * server registry and is MISSING from this interface, which is why `SuperAdminShell` reaches it
   * through `hasFeature('houses' as never)`. That cast is the drift this list exists to prevent, and it
   * silently defeats the type. Worth closing in a Band-5 follow-up; widening it here would put another
   * epic's surface inside this commit.
   */
  boardResults: boolean;
  /**
   * [REQ-AUG-30 M.3] Student leave — the family-portal apply door. Opt-in, absent ⇒ OFF,
   * enforced server-side in the leave service (the shared /leave routes carry both doors).
   * ⚠️ Its sibling registry flag `studentLeaveMinPercent` (the advisory threshold, an enum-carried
   * number-as-string) is DELIBERATELY not mirrored here: the client never computes with it — the
   * projection endpoint returns the resolved threshold and the copy's real numbers — and a
   * non-boolean key would break `hasFeature`'s boolean contract (the aiTier precedent: enum flags
   * live server-side and in the owner console only).
   */
  studentLeave: boolean;
  /**
   * [REQ-SEP-04 Band C / C1] The Academic Calendar EVENTS surface (the Events tab of the renamed
   * Holiday screen + calendar-event CRUD + the session-progress event markers). Opt-in, absent ⇒
   * OFF, enforced by requireFeatureOptIn on /calendar-events. ⚠️ The Holiday tab of the same
   * screen is deliberately NOT gated on this — holidays predate the flag and drive attendance.
   */
  academicCalendar: boolean;
  /**
   * [SHIELD-1 / S0] The public verified-parent survey module (the SA console + the public
   * `/shield/:slug` surface). Opt-in, absent ⇒ OFF.
   *
   * ⚠️ DECLARED HERE EVEN THOUGH THE PUBLIC PAGES NEVER READ IT, and that is deliberate: the SA
   * console does, and a server registry key with no interface key compiles perfectly while making
   * `hasFeature` unusable — which is how `houses` ended up reached through `hasFeature('houses' as
   * never)` a few lines above. The cast is the drift this list exists to prevent.
   */
  shieldSurvey: boolean;
  /**
   * [REQ-SEP-04 Band E / D-E-8 amended] The exam-scheduling overhaul's NET-NEW surfaces — the
   * clubbed-exam-subject console, the sitting builder, the manual grid and both exports. Opt-in,
   * absent ⇒ OFF.
   *
   * ⚠️ It does NOT gate `sittingBreakMinutes`, which shipped ungated at E1a alongside the rest of
   * ExamPolicyConfig. Declared here because the SA console reads it; a server registry key with no
   * interface key compiles clean and makes `hasFeature` unusable, which is how `houses` ended up
   * behind a `as never` cast further up this list.
   */
  examScheduleV2: boolean;
  /**
   * [DOC-AUDIT-1 S9] The school Super Admin's read-only Access Log screen. Opt-in, absent ⇒ OFF.
   * OWNER-ONLY: the server's tenant PATCH strips this key, so only the App Owner can switch it on.
   */
  docAuditSchoolView: boolean;
  /**
   * [RESULTS-UX-1] The Results desk (docs/RESULTS-UX-1-PLAN.md): with it on, ranks run by themselves and results reach
   * parents only at "Publish results". Opt-in, absent ⇒ OFF, matching requireFeatureOptIn exactly. S5 reads it for one
   * thing: the Results sheet stops calling the rankings run by itself.
   */
  resultsDesk: boolean;
  /**
   * [QPGEN-1] AI question papers (docs/QPGEN-1-PLAN.md). Absent ⇒ OFF. OWNER-ONLY: the server's tenant PATCH strips
   * this key, so only the App Owner can switch it on. No screen reads it until Q1.
   */
  qpgen: boolean;
}

/**
 * School information shape from API (`GET /schools/current`).
 *
 * [JOB B S1] Every field here is one the server actually sends. The endpoint returns the 49-column keep
 * set in `server/src/lib/school-current-view.ts` plus `phoneVerificationAvailable`; nine phantom fields
 * that no response ever carried (`code`, `logoUrl`, `currentSession`, `timezone`, `dateFormat`,
 * `currency`, `minAttendancePercent`, `periodsPerDay`, `saturdayHalfDay`) were deleted rather than left
 * to read as `undefined`. A field added here must be in that keep set, or it is a phantom again.
 */
export interface School {
  /** School UUID */
  id: string;
  /** School display name */
  name: string;
  /** Branded-link slug (aisistoo…/s/<slug>); present at runtime from /schools/current */
  slug?: string | null;
  /** School address */
  address?: string;
  /** School city */
  city?: string;
  /** School state */
  state?: string;
  /** School pincode */
  pincode?: string;
  /** School phone */
  phone?: string;
  /** School email */
  email?: string;
  /** School website */
  website?: string;
  /** Current academic year (e.g., "2024-25") */
  academicYear: string;
  /** Feature flags for this school */
  featureFlags: FeatureFlags;
  /** VERIFY-V2 C: platform capability — phone-OTP verification is available (MSG91 configured). Absent/false → phone-verify UI is fully hidden. */
  phoneVerificationAvailable?: boolean;
  /** Attendance marking mode */
  attendanceMode: AttendanceMode;
  /** Working days of the week */
  workingDays: WeekDay[];
  board?: string; principalName?: string;
  schoolLogoUrl?: string;
  recheckFeePerSubject?: string | null;
  recheckChargingModel?: 'FLAT_ON_APPLY' | 'CONDITIONAL_ON_GAIN';
  recheckGainThreshold?: number | null;
  juniorSeniorCutoff?: number | null;
  /** OLYMPIAD-P2: reserved platform (Unosonic) tenant — scopes the student portal to olympiad-only. */
  isPlatform?: boolean;
}

/**
 * School context value shape.
 */
interface SchoolContextValue {
  /** Current school data or null if not loaded */
  school: School | null;
  /** Whether school data is loading */
  isLoading: boolean;
  /** Whether school data fetch failed */
  isError: boolean;
  /** Error message if fetch failed */
  error: Error | null;
  /** Refetch school data */
  refetch: () => void;
  /** Check if a feature is enabled */
  hasFeature: (feature: keyof FeatureFlags) => boolean;
}

/**
 * Default feature flags (all disabled).
 */
/**
 * CONNECTIONS-1 — flags the SERVER grandfathers to "allowed" when the key is absent from a school's
 * featureFlags JSON (`middleware/require-feature.ts` only 403s when the key is present AND
 * disabled). The client must agree, or a grandfathered school gets a working API behind a hidden UI.
 * Deliberately excludes `diary` — the server gates that with `requireFeatureOptIn`, so absent = off.
 */
const GRANDFATHERED: ReadonlySet<keyof FeatureFlags> = new Set([
  'library', 'olympiad', 'teacherRating', 'studentConnections',
]);

const defaultFeatureFlags: FeatureFlags = {
  /*
   * [TT-WIZARD-1 S7] All three OFF here, INCLUDING `ttDiagnostics` — and that is deliberate even
   * though the server gives a brand-new school `ttDiagnostics: true`. This object is the fallback
   * for a school whose flags have not loaded, not a model of a new school: a screen that assumes a
   * feature is on before the payload arrives renders a control the school may not have.
   */
  ttDiagnostics: false,
  timetableSoftPack: false,
  timetableBranching: false,
  // BOARD-RESULT-1 — absent ⇒ OFF, matching the server's requireFeatureOptIn.
  boardResults: false,
  library: false,
  olympiad: false,
  teacherRating: false,
  electiveSubjects: false,
  studentConnections: false,
  diary: false,
  staffAttendance: false,
  sectionPeriods: false,
  teacherTiers: false,
  feeOpeningBalance: false,
  teacherWardHint: false,
  subjectHierarchy: false,
  occasionCards: false,
  occasionFestivalCards: false,
  occasionAnnualDay: false,
  staffPhotos: false,
  studentPhotos: false,
  // REQ-AUG-26 Band 3 — absent ⇒ OFF, matching requireFeatureOptIn exactly.
  transport: false,
  hostel: false,
  coaching: false,
  // [REQ-AUG-30 M.3] — absent ⇒ OFF; the threshold's absent-default is 75 (resolved server-side).
  studentLeave: false,
  // [REQ-SEP-04 Band C / C1] — absent ⇒ OFF, matching requireFeatureOptIn exactly.
  academicCalendar: false,
  // [SHIELD-1 / S0] — absent ⇒ OFF. A module that publishes a PUBLIC URL defaults off hardest.
  shieldSurvey: false,
  // [REQ-SEP-04 Band E / E1b] — absent ⇒ OFF, matching requireFeatureOptIn exactly.
  examScheduleV2: false,
  // [DOC-AUDIT-1 S9] — absent ⇒ OFF, matching requireFeatureOptIn exactly.
  docAuditSchoolView: false,
  // [RESULTS-UX-1] — absent ⇒ OFF, matching requireFeatureOptIn exactly.
  resultsDesk: false,
  // [QPGEN-1] — absent ⇒ OFF, matching requireFeatureOptIn exactly.
  qpgen: false,
};

// ============================================================================
// DEMO MODE - Set to true to bypass API and use mock data
// ============================================================================
const DEMO_MODE = false;

const DEMO_SCHOOL: School = {
  id: 'demo-school-1',
  name: 'Delhi Public School',
  address: '123 Education Lane, Sector 45',
  city: 'New Delhi',
  state: 'Delhi',
  pincode: '110001',
  phone: '+91 11 2345 6789',
  email: 'info@dps-demo.edu.in',
  website: 'https://dps-demo.edu.in',
  academicYear: '2024-25',
  featureFlags: {
    // [TT-WIZARD-1 S7] The all-on demo fixture turns them on, so the demo shows the real screens.
    ttDiagnostics: true,
    timetableSoftPack: true,
    timetableBranching: true,
    // BOARD-RESULT-1 — the DEMO fixture keeps it off, like every other opt-in flag.
    boardResults: false,
    studentLeave: false, // [M.3] — the DEMO fixture keeps opt-ins off
    academicCalendar: true, // [Band C / C1] — on in the all-on demo fixture; a calendar is worth showing
    shieldSurvey: false, // [SHIELD-1 / S0] — OFF even in the all-on fixture: it is one school's module
    examScheduleV2: false, // [Band E / E1b] — OFF even in the all-on fixture until the band ships whole
    docAuditSchoolView: false, // [DOC-AUDIT-1 S9] — OFF in the fixture: an owner-only opt-in
    resultsDesk: false, // [RESULTS-UX-1] — OFF in the fixture: it changes when parents see results
    qpgen: false, // [QPGEN-1] — OFF in the fixture: an owner-only opt-in that spends credits
    library: true,
    olympiad: true,
    // CONNECTIONS-1: the server's real key. This fixture previously set only the dead `connections`
    // key, so if DEMO_SCHOOL were ever re-enabled the Connections nav+screen would have been
    // invisible — the exact class of bug the typed key now prevents at compile time.
    studentConnections: true,
    teacherRating: true,
    electiveSubjects: true,
    diary: false,
    staffAttendance: false,
    sectionPeriods: true,
    teacherTiers: true,
    // ONBOARD-FEE-1: false even in the all-on demo fixture. Opening balances are a mid-session
    // onboarding artefact, not a capability to show off — a demo school has no pre-Aisistoo history.
    feeOpeningBalance: false,
    teacherWardHint: false,
    // OCCASION-CARDS-1: on in the all-on demo fixture — cards are a capability worth showing, unlike
    // opening balances above, which are an onboarding artefact a demo school would never have.
    subjectHierarchy: true,
    occasionCards: true,
    occasionFestivalCards: true,
    occasionAnnualDay: true,
    // PHOTO-1: on in the all-on demo fixture — faces are a capability worth showing. The fixture holds
    // no photo rows, so every surface renders its initials fallback, which is the honest demo state.
    staffPhotos: true,
    studentPhotos: true,
    // REQ-AUG-26 Band 3: on in the all-on demo fixture. The fixture holds no routes, beds or
    // enrollments, so every services surface renders its honest empty state.
    transport: true,
    hostel: true,
    coaching: true,
  },
  attendanceMode: 'DAILY',
  workingDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'],
};

/**
 * School context with default null value.
 */
const SchoolContext = createContext<SchoolContextValue | null>(null);

/**
 * Props for SchoolProvider component.
 */
interface SchoolProviderProps {
  children: ReactNode;
}

/**
 * Fetch school data from API.
 *
 * @returns School data
 */
async function fetchSchool(): Promise<School> {
  const response = await api.get<ApiResponse<School>>('/schools/current');
  return response.data.data;
}

/**
 * SchoolProvider component that wraps the application and provides school data.
 *
 * Features:
 * - Fetches school data only when user is authenticated
 * - Caches data for 5 minutes
 * - Provides feature flag checking
 *
 * Session progress is NOT computed here: it read `currentSession`, a field the server never sent, so it
 * was always 0 and nothing consumed it. The real timeline is `GET /schools/current/session-progress`
 * (SessionProgressCard).
 *
 * @example
 * ```tsx
 * <SchoolProvider>
 *   <Dashboard />
 * </SchoolProvider>
 * ```
 *
 * @param props - Component props
 * @returns Provider component
 */
export function SchoolProvider({ children }: SchoolProviderProps): JSX.Element {
  const { isAuthenticated, user } = useAuth();

  const {
    data: school,
    isLoading: queryLoading,
    isError,
    error,
    refetch,
  } = useQuery<School, Error>({
    queryKey: ['school', user?.schoolId],
    queryFn: fetchSchool,
    // Skip API call in demo mode
    enabled: !DEMO_MODE && isAuthenticated && !!user?.schoolId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });

  // Use demo school in demo mode
  const effectiveSchool = DEMO_MODE ? DEMO_SCHOOL : school;
  const isLoading = DEMO_MODE ? false : queryLoading;

  // Persist the school's display name alongside its ID (services/api.ts) so the
  // login page can show a human-readable label for a returning user next time,
  // instead of a bare UUID.
  useEffect(() => {
    if (!DEMO_MODE && school?.name) {
      setSchoolName(school.name);
    }
  }, [school?.name]);

  /**
   * Check if a feature is enabled for this school.
   *
   * CONNECTIONS-1 — the absent-key case is deliberate, and it used to disagree with the server.
   * The server GRANDFATHERS a missing key to ALLOWED (`require-feature.ts`: it only 403s when the
   * key is present AND disabled), so a school whose `featureFlags` JSON predates a flag keeps
   * working. The client did the opposite (`?? false` → hide), which meant such a school got an API
   * that serves the feature and a UI that hides the nav and hard-gates the route — invisible, and
   * exactly the kind of split-brain that's painful to debug.
   *
   * `GRANDFATHERED` lists the flags whose absence must mean "on", matching the server. Anything
   * genuinely opt-in (e.g. `diary`, which the server gates with `requireFeatureOptIn`) must NOT be
   * listed — absent stays off for those.
   */
  const hasFeature = useMemo(() => {
    return (feature: keyof FeatureFlags): boolean => {
      const flags = effectiveSchool?.featureFlags ?? defaultFeatureFlags;
      const raw = flags[feature];
      if (raw === undefined && effectiveSchool?.featureFlags) {
        // Key absent from a REAL school's flags → mirror the server's grandfathering.
        return GRANDFATHERED.has(feature);
      }
      return raw ?? false;
    };
  }, [effectiveSchool]);

  /**
   * Memoized context value.
   */
  const value = useMemo<SchoolContextValue>(
    () => ({
      school: effectiveSchool ?? null,
      isLoading,
      isError: DEMO_MODE ? false : isError,
      error: DEMO_MODE ? null : (error ?? null),
      refetch,
      hasFeature,
    }),
    [effectiveSchool, isLoading, isError, error, refetch, hasFeature]
  );

  return (
    <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>
  );
}

/**
 * Hook to access school context.
 *
 * @throws Error if used outside SchoolProvider
 * @returns School context value
 *
 * @example
 * ```tsx
 * const { school, hasFeature } = useSchoolContext();
 *
 * if (hasFeature('library')) {
 *   // Show library features
 * }
 * ```
 */
export function useSchoolContext(): SchoolContextValue {
  const context = useContext(SchoolContext);

  if (!context) {
    throw new Error('useSchoolContext must be used within a SchoolProvider');
  }

  return context;
}

export default SchoolContext;
